// hyundai_v2_2026.js
// 2026 현대차 사업장 ESG 평가 (hyundai-v2 캠페인 550~558) 결과보고서용 정적 데이터.
//
// 구조:
//   - business_site_category: vendor_code → { business_type_ko/en, country_ko/en }
//     hyundai-2025.js 와 동일한 shape. createSummaryPage 의 P2 사업장 정보표가 이걸로 채움.
//     project_code → business_type 매핑은 DB(ge_project_codes / vendor_codes) 기준:
//       HD_DOM_HQ_26               → 본사 (Headquarter)
//       HD_DOM_RI_26, HD_OV_RI_26  → 연구소 (Research Institutes)
//       HD_OV_SALES_26             → 판매법인 (Sales)
//       HD_OV_HQ_26                → 권역본부 (Regional Headquarter)
//       HD_DOM_PROD_26, HD_OV_PROD_26 → 생산법인 (Production)
//       HD_OV_COMM_PROD_26         → 상용생산법인 (Commercial Production)
//       HD_TEST_26                 → 테스트법인 (Test)
//
//   - benchmark_score: 카테고리별 avg + project_code (HD_*_26) 별 tier_avg
//     shape 은 hyundai-2025.js 와 동일.
//     category2/category3 = 2026-07-20 수령 원자료(score_calc/HD_*_26.xlsx) 로 산출한 실측
//     충족률. 둘 다 **개수 기반 충족률 + 회사별 단순평균**으로 산식 통일(2026-07-21 확정,
//     gen_compliance_rate_benchmark.py). category1 = 어느 화면도 미소비 → null.
//
//   - code_level_thresholds: ManagementLevelTable 임계값 (apps/hyundai-v2 의
//     management-level-config.json 사본).
//
//   - core_risk_indicators: PPTX P8 의 High-Risk 11 문항.

import code_level_thresholds_data from './code_level_config_hyundai_v2.json';
import code_localized_data from './code_localized_hyundai_v2.json';
import code_advice_data from './code_advice_hyundai_v2.json';
import audit_categories_data from './audit_categories_hyundai_v2.json';
import answer_localized_data from './answer_localized_hyundai_v2.json';
import answers_by_question_data from './answers_by_question_hyundai_v2.json';
import question_subjects_data from './question_subjects_hyundai_v2.json';
import summary_composition_desc_data from './summary_composition_desc_hyundai_v2.json';

export const code_level_thresholds = code_level_thresholds_data;

// code_localized: code_level_config_hyundai_v2.json 의 name/levels.defaultMessage (ko 단일)
// 대응 en/ca i18n 매핑. PDF 결과보고서 P3/P5/P6/P7 등 lang 분기 표시용.
// fallback: lang 키 없으면 ko 사용 (mixin 측 `bundle[lang] || bundle.ko`).
export const code_localized = code_localized_data;

// code_advice: 코드별 매우 미흡/미흡 등급의 '개선 권고' 텍스트 (ko 단일).
// 출처: _notes/현대_사업장/현대차 사업장 평가 ESG 진단 지표 분류_0522.xlsx 의 H/I 컬럼 마지막 행 (하늘색 셀).
// PDF P5~ 코드별 평가 결과 페이지에서 level chip 우측 별도 카드로 표시.
export const code_advice = code_advice_data;

// audit_categories: 평가 근거 (EV_*) 코드 → ko 풀텍스트 / en·ca 축약형 매핑.
// PDF 결과보고서 P10/P11 (문항별 답변) 의 '평가 근거' 컬럼 표시용. _meta 키 제외.
export const audit_categories = audit_categories_data;

// answer_localized: ko 답변 텍스트 → en/ca i18n 매핑.
// mixin 측에서 answers_by_question 으로 결정된 ko 텍스트를 normalize 후 이 매핑으로 i18n 변환.
export const answer_localized = answer_localized_data;

// answers_by_question: Q-no (Q1.9 형식) + category(satisfied/unsatisfied_no/unsatisfied_unknown/na)
//   → ko 답변 텍스트. PDF P10/P11 의 '답변 최종' 표시용.
//   백엔드 scoring row 는 ev 단위(충족 기준)라 user 가 선택한 답변 텍스트가 그대로 없음 →
//   row 의 (label, satisfied, not_applicable) 로 category 결정 후 이 매핑으로 ko 텍스트 조회.
//   카테고리 룰:
//     not_applicable=true                          → na
//     satisfied=true                               → satisfied (예)
//     satisfied=false, ev_name '확인되지 않음' 키워드 → unsatisfied_unknown (모름)
//     그 외 (satisfied=false)                       → unsatisfied_no (아니오)
export const answers_by_question = answers_by_question_data;

// question_subjects: Q-no → 주제 i18n. PDF P10/P11 '문항' 컬럼의 'Q1.9: ~~' 뒷부분.
export const question_subjects = question_subjects_data;

// summary_composition_desc: Summary 페이지 첫 표(사업장·평가 정보) 다음의 desc 문구.
//   ko/en/ca 단일 공통 템플릿. project_code 무관 (지표 개수만 법인별로 다른데, 그 값은
//   scoring data(category1_scoring_pd '문항 수')에서 동적으로 채움).
//   placeholder: {company_name} {human_count} {ethics_count} {total_count}.
//   출처: _notes/현대_사업장/(QESG) 2026 현대차 사업장 평가 결과보고서 수정 사항 (0615).xlsx.
//   ko 는 엑셀 원문, en/ca 는 ko 의미에 맞춰 작성 (검수 필요).
//   consume: report.hyundai_v2.mixin.js → sub_imporatance_composition_description().
export const summary_composition_desc = summary_composition_desc_data;

// ────────────────────────────────────────────────────────────────
// business_site_category — DB vendor_codes × companies 기반 매핑
// ────────────────────────────────────────────────────────────────
export const business_site_category = {
    // HD_DOM_HQ_26 — 본사
    'aec21e': { business_type_ko: '본사', country_ko: '한국', business_type_en: 'Headquarter', country_en: 'Republic of Korea' },

    // HD_DOM_PROD_26 — 생산법인 (국내)
    '9754bf': { business_type_ko: '생산법인', country_ko: '한국', business_type_en: 'Production', country_en: 'Republic of Korea' },
    '1d2886': { business_type_ko: '생산법인', country_ko: '한국', business_type_en: 'Production', country_en: 'Republic of Korea' },
    'b2d392': { business_type_ko: '생산법인', country_ko: '한국', business_type_en: 'Production', country_en: 'Republic of Korea' },

    // HD_DOM_RI_26 — 연구소 (국내)
    '7cd309': { business_type_ko: '연구소', country_ko: '한국', business_type_en: 'Research Institutes', country_en: 'Republic of Korea' },

    // HD_OV_COMM_PROD_26 — 상용생산법인 (해외)
    '4b8d65': { business_type_ko: '상용생산법인', country_ko: '멕시코', business_type_en: 'Commercial Production', country_en: 'Mexico' },
    '76ce51': { business_type_ko: '상용생산법인', country_ko: '베트남', business_type_en: 'Commercial Production', country_en: 'Vietnam' },
    '812ffb': { business_type_ko: '상용생산법인', country_ko: '중국', business_type_en: 'Commercial Production', country_en: 'China' },
    '9f43f5': { business_type_ko: '상용생산법인', country_ko: '중국', business_type_en: 'Commercial Production', country_en: 'China' },

    // HD_OV_HQ_26 — 권역본부 (해외)
    '27fd2f': { business_type_ko: '권역본부', country_ko: '인도', business_type_en: 'Regional Headquarter', country_en: 'India' },
    '593020': { business_type_ko: '권역본부', country_ko: '독일', business_type_en: 'Regional Headquarter', country_en: 'Germany' },
    'c95d83': { business_type_ko: '권역본부', country_ko: '아랍에미레이트', business_type_en: 'Regional Headquarter', country_en: 'United Arab Emirates' },
    'f56d90': { business_type_ko: '권역본부', country_ko: '중국', business_type_en: 'Regional Headquarter', country_en: 'China' },

    // HD_OV_PROD_26 — 생산법인 (해외)
    '0d3eda': { business_type_ko: '생산법인', country_ko: '인도', business_type_en: 'Production', country_en: 'India' },
    '15bc3d': { business_type_ko: '생산법인', country_ko: '인도네시아', business_type_en: 'Production', country_en: 'Indonesia' },
    '73e702': { business_type_ko: '생산법인', country_ko: '싱가포르', business_type_en: 'Production', country_en: 'Singapore' },
    '7db288': { business_type_ko: '생산법인', country_ko: '튀르키예', business_type_en: 'Production', country_en: 'Türkiye' },
    '93d78f': { business_type_ko: '생산법인', country_ko: '인도', business_type_en: 'Production', country_en: 'India' },
    'b05147': { business_type_ko: '생산법인', country_ko: '미국', business_type_en: 'Production', country_en: 'United States' },
    'b95fac': { business_type_ko: '생산법인', country_ko: '베트남', business_type_en: 'Production', country_en: 'Vietnam' },
    'ce1ef6': { business_type_ko: '생산법인', country_ko: '체코', business_type_en: 'Production', country_en: 'Czechia' },
    'd35aa6': { business_type_ko: '생산법인', country_ko: '브라질', business_type_en: 'Production', country_en: 'Brazil' },
    'eca45f': { business_type_ko: '생산법인', country_ko: '중국', business_type_en: 'Production', country_en: 'China' },
    'f6163a': { business_type_ko: '생산법인', country_ko: '미국', business_type_en: 'Production', country_en: 'United States' },

    // HD_OV_RI_26 — 연구소 (해외)
    '3d75f3': { business_type_ko: '연구소', country_ko: '중국', business_type_en: 'Research Institutes', country_en: 'China' },
    '6327cf': { business_type_ko: '연구소', country_ko: '미국', business_type_en: 'Research Institutes', country_en: 'United States' },
    '6791cb': { business_type_ko: '연구소', country_ko: '독일', business_type_en: 'Research Institutes', country_en: 'Germany' },
    '9858f0': { business_type_ko: '연구소', country_ko: '중국', business_type_en: 'Research Institutes', country_en: 'China' },
    'f35821': { business_type_ko: '연구소', country_ko: '인도', business_type_en: 'Research Institutes', country_en: 'India' },

    // HD_OV_SALES_26 — 판매법인 (해외)
    '095447': { business_type_ko: '판매법인', country_ko: '인도네시아', business_type_en: 'Sales', country_en: 'Indonesia' },
    '12a5dc': { business_type_ko: '판매법인', country_ko: '캐나다', business_type_en: 'Sales', country_en: 'Canada' },
    '166732': { business_type_ko: '판매법인', country_ko: '프랑스', business_type_en: 'Sales', country_en: 'France' },
    '1ab61b': { business_type_ko: '판매법인', country_ko: '독일', business_type_en: 'Sales', country_en: 'Germany' },
    '1bfd38': { business_type_ko: '판매법인', country_ko: '이탈리아', business_type_en: 'Sales', country_en: 'Italy' },
    '3d8abe': { business_type_ko: '판매법인', country_ko: '필리핀', business_type_en: 'Sales', country_en: 'Philippines' },
    '423371': { business_type_ko: '판매법인', country_ko: '호주', business_type_en: 'Sales', country_en: 'Australia' },
    '572f2f': { business_type_ko: '판매법인', country_ko: '미국', business_type_en: 'Sales', country_en: 'United States' },
    '64b320': { business_type_ko: '판매법인', country_ko: '독일', business_type_en: 'Sales', country_en: 'Germany' },
    '716af9': { business_type_ko: '판매법인', country_ko: '말레이시아', business_type_en: 'Sales', country_en: 'Malaysia' },
    '77c0af': { business_type_ko: '판매법인', country_ko: '스웨덴', business_type_en: 'Sales', country_en: 'Sweden' },
    '914950': { business_type_ko: '판매법인', country_ko: '미국', business_type_en: 'Sales', country_en: 'United States' },
    'a65c0e': { business_type_ko: '판매법인', country_ko: '체코', business_type_en: 'Sales', country_en: 'Czechia' },
    'af8ebc': { business_type_ko: '판매법인', country_ko: '스페인', business_type_en: 'Sales', country_en: 'Spain' },
    'b20e9a': { business_type_ko: '판매법인', country_ko: '스위스', business_type_en: 'Sales', country_en: 'Switzerland' },
    'e5423c': { business_type_ko: '판매법인', country_ko: '독일', business_type_en: 'Sales', country_en: 'Germany' },
    'e67713': { business_type_ko: '판매법인', country_ko: '태국', business_type_en: 'Sales', country_en: 'Thailand' },
    'e89554': { business_type_ko: '판매법인', country_ko: '네덜란드', business_type_en: 'Sales', country_en: 'Netherlands' },
    'f55815': { business_type_ko: '판매법인', country_ko: '영국', business_type_en: 'Sales', country_en: 'United Kingdom' },
    'f6b443': { business_type_ko: '판매법인', country_ko: '폴란드', business_type_en: 'Sales', country_en: 'Poland' },
    'f8c15d': { business_type_ko: '판매법인', country_ko: '중국', business_type_en: 'Sales', country_en: 'China' },
    'ff3dea': { business_type_ko: '판매법인', country_ko: '멕시코', business_type_en: 'Sales', country_en: 'Mexico' },

    // HD_TEST_26 — 테스트법인
    'AFAGPG': { business_type_ko: '테스트법인', country_ko: '슬로바키아', business_type_en: 'Test', country_en: 'Slovakia' },
    'BANS9V': { business_type_ko: '테스트법인', country_ko: '한국', business_type_en: 'Test', country_en: 'Republic of Korea' },
    'I8YDHG': { business_type_ko: '테스트법인', country_ko: '러시아', business_type_en: 'Test', country_en: 'Russia' },
    'KFKKRT': { business_type_ko: '테스트법인', country_ko: '중국', business_type_en: 'Test', country_en: 'China' },
    'NIL4H3': { business_type_ko: '테스트법인', country_ko: '미국', business_type_en: 'Test', country_en: 'United States' },
    'PWHE6I': { business_type_ko: '테스트법인', country_ko: '인도', business_type_en: 'Test', country_en: 'India' }
};

// ────────────────────────────────────────────────────────────────
// benchmark_score — project_code (HD_*_26) 별 tier_avg.
// v1 hyundai-2025 와 shape 동일.
// ────────────────────────────────────────────────────────────────
const _v2_project_codes = [
    'HD_DOM_HQ_26',
    'HD_DOM_PROD_26',
    'HD_DOM_RI_26',
    'HD_OV_HQ_26',
    'HD_OV_PROD_26',
    'HD_OV_COMM_PROD_26',
    'HD_OV_RI_26',
    'HD_OV_SALES_26',
    'HD_TEST_26'
];

const _emptyTierAvg = () => _v2_project_codes.reduce((o, k) => { o[k] = null; return o; }, {});
const _emptyBenchEntry = () => ({ avg: null, tier_avg: _emptyTierAvg() });

// ────────────────────────────────────────────────────────────────
// category2 (대분류, A-1~A-5·B-1) 실측 충족률 — 2026 캠페인 종료 후 확정치.
//
//   출처: _notes/현대_사업장/score_calc/HD_*_26.xlsx 의 Scores_Questions(문항 단위 원점수, 2026-07-20 수령)
//       + scoring_data/hd_scoring_all.csv (sub_code→category3_question_name 구조 매핑, project_code 단위)
//   생성: _notes/현대_사업장/gen_compliance_rate_benchmark.py (엑셀 갱신 시 재실행 → 아래 블록 교체)
//
//   category1/category3(Code) 와 동일하게 **개수 기반 충족률**(hyundaiComplianceRates 와 동일 정의:
//   문항 achieved>0 → 충족, ==0 → 미충족, '해당없음' → 분모 제외)로 통일했다 — 애초에 score_calc 의
//   points(배점) 로 계산했던 건 오판이었음(당 사업장 실선이 count 기반인데 benchmark 만 score 기반이면
//   레이더에서 서로 다른 지표를 비교하게 됨).
//   2단계 산출:
//     1) 회사 단위 — 회사의 A-1(category2) 충족률 = 그 회사가 가진 A-1-1~A-1-5(Code) 문항들의
//        (충족 문항 수 ÷ 적용 문항 수) × 100.
//     2) 티어 단위 — 회사별 값을 **회사 개수로 나눈 단순 산술평균**(동일 가중, pool 아님 —
//        포인트/문항 볼륨이 큰 회사가 티어 평균을 과대표하지 않도록).
//   검산: 이 스크립트로 category3(Code) 를 동일 산식으로 재계산해 2026_CODE별_평균충족률.xlsx
//   과 대조 — 2026-07-31 마스터 엑셀 'CODE별 충족률' 시트도 pooled → 회사별 단순평균으로
//   전환해서, 189셀(21 Code × 9열)이 아래 값과 **정확히 일치**한다(불일치 0).
//
//   phantom row 주의(memory: project_hyundai_v2_core_phantom_row.md) — H1.49v2/50/52/53/54/55/57
//   (A-4-1~3) 은 HD_DOM_RI_26/HD_OV_HQ_26/HD_OV_RI_26/HD_OV_SALES_26 에서 실제 질문지에 없는
//   유령 문항인데 raw score_calc 에는 achieved=0(미충족)으로 잡혀있다. sub_code→Code 매핑을
//   project_code 단위로 만들어 (해당 project_code 에서 category3_question_name 이 빈 값인
//   유령 조합은 매핑 자체가 없어 자동 skip) 해결 — 전역 매핑을 쓰면 A-4 tier_avg 가 크게
//   왜곡된다(A-4-1 전체 평균이 90.3%→34.0%로 잘못 떨어지는 걸 확인).
//
//   B-2(그린워싱)는 리포트에서 전면 제외되는 영역이라 항상 null.
//   null 의미: tier_avg[pc] = null → 해당 project_code 에 적용 문항 없음. HD_TEST_26 은 데이터 없음.
// ────────────────────────────────────────────────────────────────
const _category2_benchmark = {
    // 0731 남양연구소(HD_DOM_RI_26 유일 법인) A-1-5 재평가 충족 반영 → tier 91.7→100, avg 91.3→91.5
    'A-1. 인권경영 시스템':
        { avg: 91.5, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 91.9, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 98.1, 'HD_OV_PROD_26': 92.5, 'HD_OV_COMM_PROD_26': 81.8, 'HD_OV_RI_26': 91.1, 'HD_OV_SALES_26': 90.8, 'HD_TEST_26': null } },
    'A-2. 인권 리스크 관리':
        { avg: 93.6, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 98.1, 'HD_OV_PROD_26': 95.3, 'HD_OV_COMM_PROD_26': 88.5, 'HD_OV_RI_26': 89.2, 'HD_OV_SALES_26': 92.3, 'HD_TEST_26': null } },
    'A-3. 근로환경 개선':
        { avg: 91.8, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 93, 'HD_OV_PROD_26': 99.2, 'HD_OV_COMM_PROD_26': 77.1, 'HD_OV_RI_26': 85.5, 'HD_OV_SALES_26': 90.1, 'HD_TEST_26': null } },
    'A-4. 공급망 인권/노동 관리':
        { avg: 93, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': null, 'HD_OV_HQ_26': null, 'HD_OV_PROD_26': 97, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': null, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } },
    'A-5. 생활환경 인권 관리':
        { avg: 92.6, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 83.3, 'HD_OV_HQ_26': 80, 'HD_OV_PROD_26': 94.5, 'HD_OV_COMM_PROD_26': 93.3, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': 85.7, 'HD_TEST_26': null } },
    'B-1. 윤리경영 시스템':
        { avg: 92, tier_avg: { 'HD_DOM_HQ_26': 90, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 97.5, 'HD_OV_PROD_26': 99.1, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 86, 'HD_OV_SALES_26': 90.5, 'HD_TEST_26': null } },
    'B-2. 그린워싱 리스크 관리':
        { avg: null, tier_avg: { 'HD_DOM_HQ_26': null, 'HD_DOM_PROD_26': null, 'HD_DOM_RI_26': null, 'HD_OV_HQ_26': null, 'HD_OV_PROD_26': null, 'HD_OV_COMM_PROD_26': null, 'HD_OV_RI_26': null, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } }
};

// ────────────────────────────────────────────────────────────────
// category3 (Code) 실측 충족률 — 2026 캠페인(550~558) 종료 후 확정치.
//
//   출처: _notes/현대_사업장/score_calc/HD_*_26.xlsx 의 Scores_Questions(문항 단위 원점수, 2026-07-20 수령)
//       + scoring_data/hd_scoring_all.csv (sub_code→category3_question_name 구조 매핑, project_code 단위)
//   생성: _notes/현대_사업장/gen_compliance_rate_benchmark.py (엑셀 갱신 시 재실행 → 아래 블록 교체)
//   산식: category2 와 동일한 **개수 기반 충족률 + 회사별 단순평균**(2026-07-21 확정, category2/category3
//         통일). 문항 achieved>0 → 충족, ==0 → 미충족, '해당없음' → 분모 제외.
//         2단계: 1) 회사 단위 — 회사의 Code 충족률 = 그 Code 문항들의 (충족 수 ÷ 적용 수) × 100.
//                2) 티어 단위 — 회사별 값을 회사 개수로 나눈 단순 산술평균(동일 가중, pool 아님).
//         '당 사업장' 막대(hyundaiComplianceRates)와 동일 정의라 직접 비교 가능.
//   단위: 백분율(0~100), 소수 1자리.
//
//   이전엔 2026_CODE별_평균충족률.xlsx(마스터 엑셀, pooled 방식)를 그대로 썼으나, 대분류(category2)와
//   산식이 갈리는 문제가 있어 회사별 단순평균으로 통일했다(2026-07-21).
//   2026-07-31: 마스터 엑셀의 'CODE별 충족률' 시트도 같은 산식으로 전환 — 엑셀만 pooled 로 남아
//   결과보고서와 값이 달라(예: A-1-5 전체 평균 엑셀 80.8% vs 리포트 78.9%) 혼선을 빚었기 때문.
//   이제 엑셀 두 시트·생성 스크립트·이 const 가 전부 동일 산식이다.
//   (두 방식의 과거 차이 분석은 _notes/현대_사업장/tier_avg_method_review.html 참고.)
//
//   phantom row 주의(memory: project_hyundai_v2_core_phantom_row.md) — H1.49v2/50/52/53/54/55/57
//   (A-4-1~3) 은 HD_DOM_RI_26/HD_OV_HQ_26/HD_OV_RI_26/HD_OV_SALES_26 에서 실제 질문지에 없는
//   유령 문항인데 raw score_calc 에는 achieved=0(미충족)으로 잡혀있다. sub_code→Code 매핑을
//   project_code 단위로 만들어 해결(전역 매핑을 쓰면 A-4 tier_avg 가 크게 왜곡된다).
//
//   키는 반드시 **DB 의 category3_question_name(한글)** — 조회가
//   benchMarkScore.category3[category3_question_name_kr] 로 일어난다.
//
//   null 의미:
//     - tier_avg[pc] = null  → 해당 project_code 에 적용 문항 없음. 막대 N/A.
//     - HD_TEST_26           → 테스트 법인. 데이터 없음 → 항상 null.
//   B-2-1(그린워싱)이 없는 것은 정상 — 결과보고서에서 B-2 영역을 전면 제외한다
//   (report.hyundai_v2.mixin.js 의 HYUNDAI_V2_EXCLUDED_SUB_CODES 참고).
// ────────────────────────────────────────────────────────────────
const _category3_benchmark = {
    'A-1-1. 인권/노동 정책 및 관리 시스템':
        { avg: 90.2, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 93.8, 'HD_OV_PROD_26': 91.7, 'HD_OV_COMM_PROD_26': 68.8, 'HD_OV_RI_26': 95, 'HD_OV_SALES_26': 89.4, 'HD_TEST_26': null } },
    'A-1-2. 근로 조건의 정의':
        { avg: 97.4, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 97, 'HD_OV_COMM_PROD_26': 100, 'HD_OV_RI_26': 86.7, 'HD_OV_SALES_26': 98.5, 'HD_TEST_26': null } },
    'A-1-3. 근로시간 관리':
        { avg: 97.4, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 97, 'HD_OV_COMM_PROD_26': 91.7, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': 97, 'HD_TEST_26': null } },
    'A-1-4. 임금 지급':
        { avg: 85.6, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 84.8, 'HD_OV_COMM_PROD_26': 66.7, 'HD_OV_RI_26': 86.7, 'HD_OV_SALES_26': 83.3, 'HD_TEST_26': null } },
    // 0731 A-1-5 재평가 2건 반영(운영 dump 실측 campaign_base_scoring 과 일치 확인):
    //   남양연구소(HD_DOM_RI_26 유일 법인)   미충족→충족  tier 0→100
    //   현대자동차 본사(HD_DOM_HQ_26 유일 법인) 해당없음→충족 tier null→100
    //   → avg 75.7→78.4→78.9
    'A-1-5. 사내도급 근로자 인권 관리':
        { avg: 78.9, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 50, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 95, 'HD_OV_COMM_PROD_26': 100, 'HD_OV_RI_26': 75, 'HD_OV_SALES_26': 66.7, 'HD_TEST_26': null } },
    'A-2-1. 비인도적 행위 금지':
        { avg: 95.4, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 97, 'HD_OV_COMM_PROD_26': 91.7, 'HD_OV_RI_26': 93.3, 'HD_OV_SALES_26': 93.9, 'HD_TEST_26': null } },
    'A-2-2. 공정한 채용':
        { avg: 80.4, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 87.5, 'HD_OV_PROD_26': 86.4, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 70, 'HD_OV_SALES_26': 75, 'HD_TEST_26': null } },
    'A-2-3. 아동노동 금지':
        { avg: 92.8, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 93.9, 'HD_OV_COMM_PROD_26': 87.5, 'HD_OV_RI_26': 90, 'HD_OV_SALES_26': 90.9, 'HD_TEST_26': null } },
    'A-2-4. 강제노동 금지':
        { avg: 97.4, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 98.5, 'HD_OV_COMM_PROD_26': 91.7, 'HD_OV_RI_26': 93.3, 'HD_OV_SALES_26': 97.7, 'HD_TEST_26': null } },
    'A-3-1. 노사 커뮤니케이션':
        { avg: 83.7, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 83.3, 'HD_OV_PROD_26': 97, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 60, 'HD_OV_SALES_26': 80.3, 'HD_TEST_26': null } },
    'A-3-2. 근로자 역량 강화 지원':
        { avg: 98, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 100, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': 95.5, 'HD_TEST_26': null } },
    'A-3-3. 근로자 출산 및 육아 지원':
        { avg: 96.1, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': 95.5, 'HD_TEST_26': null } },
    'A-3-4. 시설 위생 관리':
        { avg: 96.1, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 80, 'HD_OV_SALES_26': 100, 'HD_TEST_26': null } },
    'A-3-5. 고충처리 매커니즘':
        { avg: 92.9, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 95, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 88, 'HD_OV_SALES_26': 91.8, 'HD_TEST_26': null } },
    'A-4-1. 협력사 행동규범':
        { avg: 89.5, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': null, 'HD_OV_HQ_26': null, 'HD_OV_PROD_26': 90.9, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': null, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } },
    'A-4-2. 공급망 리스크 관리':
        { avg: 100, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': null, 'HD_DOM_RI_26': null, 'HD_OV_HQ_26': null, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': null, 'HD_OV_RI_26': null, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } },
    'A-4-3. 분쟁광물 관리':
        { avg: 100, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': null, 'HD_DOM_RI_26': null, 'HD_OV_HQ_26': null, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': null, 'HD_OV_RI_26': null, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } },
    'A-5-1. 기숙사 내 인권 및 안전 관리':
        { avg: 86.9, tier_avg: { 'HD_DOM_HQ_26': null, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 80, 'HD_OV_HQ_26': 50, 'HD_OV_PROD_26': 81.2, 'HD_OV_COMM_PROD_26': 87.5, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': null, 'HD_TEST_26': null } },
    'A-5-2. 식당 내 인권 및 위생 관리':
        { avg: 96.8, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 100, 'HD_OV_RI_26': 100, 'HD_OV_SALES_26': 85.7, 'HD_TEST_26': null } },
    'B-1-1. 윤리 정책 및 관리 시스템':
        { avg: 92.2, tier_avg: { 'HD_DOM_HQ_26': 100, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 95, 'HD_OV_PROD_26': 98.2, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 80, 'HD_OV_SALES_26': 92.7, 'HD_TEST_26': null } },
    'B-1-2. 윤리신고 매커니즘':
        { avg: 91.8, tier_avg: { 'HD_DOM_HQ_26': 80, 'HD_DOM_PROD_26': 100, 'HD_DOM_RI_26': 100, 'HD_OV_HQ_26': 100, 'HD_OV_PROD_26': 100, 'HD_OV_COMM_PROD_26': 75, 'HD_OV_RI_26': 92, 'HD_OV_SALES_26': 88.2, 'HD_TEST_26': null } }
};

export const benchmark_score = {
    // category1 — 현재 어떤 화면에서도 소비하지 않음 (mixin 은 category2/category3 만 조회).
    //   shape 유지용으로만 남기고 값은 null.
    category1: {
        '1. 인권 및 노동': _emptyBenchEntry(),
        '2. 윤리': _emptyBenchEntry()
    },
    // category2 — 레이더 차트(P4) 의 'Tier 평균'/'전체 평균' 라인. 실측 평균(score_calc 폴더 기반).
    category2: _category2_benchmark,
    category3: _category3_benchmark
};

// ────────────────────────────────────────────────────────────────
// core_risk_indicators — PPTX P8 (Core 지표 충족 여부) 의 11문항.
// Excel "(QESG) 2026 현대차 사업장 평가 ESG 진단 지표 분류_결과보고서용.xlsx"
// '2026 지표' 시트 CORE 컬럼 = 'CORE' 인 row 11개와 1:1 매핑.
//
// 필드 설명:
//   - db_sub_code: DB 의 df_subs.sub_code (H1.Xv2 형식). scoring 충족 여부 조회용.
//                  df_subs_init.risk = 1 도 이 코드와 join.
//                  (참고: Excel Q-넘버와 DB H-넘버는 일부 불일치하므로
//                   migration `hyundai_v2_2026_core_flag_fix.sql` 로 정합화 완료)
//   - category3_code: 'A-1-1' 형식. P8 표의 Code 컬럼 표시.
//   - category3_name: PPTX 표 'Code' 셀에 category3_code 와 함께 표기되는 이름.
//   - question_no: Excel 의 'Q 1.9' 형식. P8 표의 '문항' 컬럼.
//   - indicator: 지표명 (Excel col11 의 'Q 1.9: ~~' 에서 콜론 뒤 텍스트).
//   - risk: 미충족 시 P8 표 '비고' 셀에 표기될 CORE 미충족 문구 (Excel col14).
// ────────────────────────────────────────────────────────────────
export const core_risk_indicators = [
    { no: 1, db_sub_code: 'H1.9v2', category3_code: 'A-1-1',
        category3_name: { ko: '인권/노동 정책 및 관리 시스템', en: 'Human Rights/Labor Policy and Management System', ca: '人权/劳动政策及管理体系' },
        question_no: 'Q1.9',
        indicator: { ko: '인권 정책 제정 또는 번역 보유', en: 'Establishment or Localized Translation of Human Rights Policy', ca: '人权政策制定或翻译版本持有' },
        risk: {
            ko: '현지어 인권 정책을 보유하지 않으면 UN 기업과 인권 이행원칙(UNGPs) 및 OECD 다국적기업 가이드라인상 인권 실사 의무를 이행하지 못한 것으로 간주됩니다.',
            en: 'Failure to maintain a localized human rights policy is regarded as non-fulfillment of human rights due diligence obligations under the UN Guiding Principles on Business and Human Rights (UNGPs) and the OECD Guidelines for Multinational Enterprises.',
            ca: '不持有当地语言的人权政策将被视为未履行《联合国工商业与人权指导原则》(UNGPs)及《经合组织跨国企业准则》所要求的人权尽职调查义务。'
        }
    },
    { no: 2, db_sub_code: 'H1.13v2', category3_code: 'A-1-2',
        category3_name: { ko: '근로 조건의 정의', en: 'Definition of Working Conditions', ca: '工作条件的界定' },
        question_no: 'Q1.13',
        indicator: { ko: '근로계약서 작성 및 교부', en: 'Preparation and Delivery of Written Labor Contracts', ca: '签订并交付劳动合同' },
        risk: {
            ko: '근로계약서 미작성·미교부는 ILO 협약 제95호 및 대부분의 현지 노동법상 위반으로, 행정 제재 및 민사 분쟁 발생 시 사용자에게 불리한 법적 입증 책임이 부과됩니다.',
            en: 'Failure to prepare and deliver written labor contracts violates ILO Convention No. 95 and most local labor laws, exposing employers to administrative sanctions and adverse burden of proof in civil disputes.',
            ca: '未签订或未交付劳动合同违反《国际劳工组织第95号公约》及多数当地劳动法，将面临行政处罚，并在民事纠纷中由雇主承担不利举证责任。'
        }
    },
    { no: 3, db_sub_code: 'H1.14v2', category3_code: 'A-1-2',
        category3_name: { ko: '근로 조건의 정의', en: 'Definition of Working Conditions', ca: '工作条件的界定' },
        question_no: 'Q1.14',
        indicator: { ko: '급여명세서 제공', en: 'Issuance of Wage Statements', ca: '提供工资明细单' },
        risk: {
            ko: '급여명세서 미제공은 ILO 협약 제95호(임금 보호)가 요구하는 임금 투명성 의무를 위반하며, 현지 노동법상 임금 착취·부당공제 입증을 어렵게 해 형사 제재 대상이 될 수 있습니다.',
            en: 'Failure to issue wage statements breaches the wage-transparency duty under ILO Convention No. 95 (Protection of Wages), hindering the proof of wage exploitation or unlawful deduction under local labor law and exposing the employer to criminal sanctions.',
            ca: '未提供工资明细单违反《国际劳工组织第95号公约》(工资保护)所要求的工资透明度义务，使当地劳动法下的工资剥削与非法扣减难以举证，可能面临刑事处罚。'
        }
    },
    { no: 4, db_sub_code: 'H1.16v2', category3_code: 'A-1-3',
        category3_name: { ko: '근로시간 관리', en: 'Working Hours Management', ca: '工作时间管理' },
        question_no: 'Q1.16',
        indicator: { ko: '근로시간 적법 기록 및 관리', en: 'Lawful Recording and Management of Working Hours', ca: '依法记录与管理工作时间' },
        risk: {
            ko: '근로시간 미기록은 ILO 협약 제1호·제30호(근로시간 제한) 및 현지 노동법상 법정 근로시간·연장수당 지급 의무 위반의 직접적 근거가 되어 행정 처분 및 형사 처벌 대상이 됩니다.',
            en: 'Failure to record working hours is direct evidence of violating statutory working-hour limits and overtime-pay obligations under ILO Conventions No. 1/30 and local labor law, resulting in administrative and criminal liability.',
            ca: '未记录工作时间将直接构成违反《国际劳工组织第1号·第30号公约》(工作时间限制)及当地劳动法关于法定工时与加班津贴支付义务的依据，将面临行政处分及刑事处罚。'
        }
    },
    { no: 5, db_sub_code: 'H1.19v2', category3_code: 'A-1-4',
        category3_name: { ko: '임금 지급', en: 'Wage Payment', ca: '工资支付' },
        question_no: 'Q1.19',
        indicator: { ko: '법정 최저임금 이상 급여 지급', en: 'Payment of Wages at or Above the Statutory Minimum', ca: '支付不低于法定最低工资的薪酬' },
        risk: {
            ko: '법정 최저임금 미지급은 ILO 협약 제131호(최저임금 결정) 및 현지 노동법상 가장 중한 위반 중 하나로, 형사 처벌·과태료 부과 및 미지급 임금 소급 청구 의무가 발생합니다.',
            en: 'Underpayment of the statutory minimum wage is among the gravest violations under ILO Convention No. 131 (Minimum Wage Fixing) and local labor law, triggering criminal penalties, administrative fines, and back-pay liability.',
            ca: '未支付法定最低工资属于《国际劳工组织第131号公约》(最低工资确定)及当地劳动法下最严重的违法行为之一，将面临刑事处罚、行政罚款及补付欠薪义务。'
        }
    },
    // Q1.22(사내도급 점검 체계)의 실제 sub_code 는 H1.22_n (label Q1.22). H1.22v2 는 Q1.23(점검 활동)이라 오매핑이었음 → 수정.
    { no: 6, db_sub_code: 'H1.22_n', category3_code: 'A-1-5',
        category3_name: { ko: '사내도급 근로자 인권 관리', en: 'Human Rights Management of On-Site Contractor Workers', ca: '场内承包工人的人权管理' },
        question_no: 'Q1.22',
        indicator: { ko: '사내도급사 인권 리스크 점검 체계', en: 'On-Site Contractor Human Rights Risk Assessment System', ca: '场内承包商人权风险点检体系' },
        risk: {
            ko: '사내도급사 점검 체계 부재는 EU 공급망 실사 지침(CSDDD) 및 ILO 협약 제94호가 요구하는 공급망 내 노동 기준 준수 관리 의무를 이행하지 못한 것으로 간주됩니다.',
            en: 'Absence of an on-site contractor assessment system is deemed a failure to fulfill supply-chain labor-standards oversight duties required by the EU Corporate Sustainability Due Diligence Directive (CSDDD) and ILO Convention No. 94.',
            ca: '缺少场内承包商点检体系将被视为未履行《欧盟企业可持续性尽职调查指令》(CSDDD)及《国际劳工组织第94号公约》所要求的供应链劳动标准合规管理义务。'
        }
    },
    { no: 7, db_sub_code: 'H1.28v2', category3_code: 'A-2-3',
        category3_name: { ko: '아동노동 금지', en: 'Prohibition of Child Labor', ca: '禁止童工' },
        question_no: 'Q1.29',
        indicator: { ko: '법적 최소 근로 연령 준수 및 연령 확인', en: 'Compliance with the Statutory Minimum Working Age and Age Verification', ca: '遵守法定最低就业年龄并进行年龄核实' },
        risk: {
            ko: '아동 고용 연령 미확인은 ILO 협약 제138호(최저 고용 연령)·제182호(최악의 형태 아동노동 금지) 위반으로, 당사국 노동법상 형사 처벌 및 영업 정지 대상이 됩니다.',
            en: 'Failure to verify the legal minimum employment age violates ILO Conventions No. 138 (Minimum Age) and No. 182 (Worst Forms of Child Labour), leading to criminal sanctions and business suspension under domestic labor law.',
            ca: '未核实最低就业年龄违反《国际劳工组织第138号公约》(最低就业年龄)及第182号公约(禁止最恶劣形式的童工劳动)，将依当事国劳动法面临刑事处罚及停业处分。'
        }
    },
    { no: 8, db_sub_code: 'H1.44v2', category3_code: 'A-3-5',
        category3_name: { ko: '고충처리 매커니즘', en: 'Grievance Mechanism', ca: '申诉处理机制' },
        question_no: 'Q1.46',
        indicator: { ko: '공개된 고충 접수 채널 운영', en: 'Operation of a Publicly Accessible Grievance Channel', ca: '运营公开的申诉受理渠道' },
        risk: {
            ko: '고충 접수 채널 미운영은 UN 기업과 인권 이행원칙(UNGPs) 제3축(구제에 대한 접근)이 요구하는 실효적 구제 메커니즘 의무를 충족하지 못한 것으로 간주됩니다.',
            en: 'Failure to operate a grievance channel is regarded as non-compliance with the effective-remedy obligation under Pillar III (Access to Remedy) of the UN Guiding Principles on Business and Human Rights (UNGPs).',
            ca: '未运营申诉受理渠道将被视为未满足《联合国工商业与人权指导原则》(UNGPs)第三支柱(获得救济)所要求的有效救济机制义务。'
        }
    },
    { no: 9, db_sub_code: 'H1.49v2', category3_code: 'A-4-1',
        category3_name: { ko: '협력사 행동규범', en: 'Supplier Code of Conduct', ca: '供应商行为准则' },
        question_no: 'Q1.51',
        indicator: { ko: '현지어 협력사 행동규범 보유', en: 'Maintenance of a Localized Supplier Code of Conduct', ca: '持有当地语言版本的供应商行为准则' },
        risk: {
            ko: '현지어 협력사 행동규범 미보유는 EU 공급망 실사 지침(CSDDD) 및 OECD 실사 가이드라인이 요구하는 공급망 내 인권·노동 기준 전파 의무를 이행하지 못한 것으로 간주됩니다.',
            en: 'Absence of a localized supplier code of conduct is deemed a failure to fulfill the duty to disseminate supply-chain human-rights and labor standards required by the EU CSDDD and the OECD Due Diligence Guidance.',
            ca: '未持有当地语言版本的供应商行为准则将被视为未履行《欧盟企业可持续性尽职调查指令》(CSDDD)及《经合组织尽职调查指南》所要求的供应链内人权与劳动标准传达义务。'
        }
    },
    { no: 10, db_sub_code: 'H2.1v2', category3_code: 'B-1-1',
        category3_name: { ko: '윤리 정책 및 관리 시스템', en: 'Ethics Policy and Management System', ca: '伦理政策及管理体系' },
        question_no: 'Q2.1',
        indicator: { ko: '윤리 원칙 제정 또는 번역 보유', en: 'Establishment or Localized Translation of Ethics Principles', ca: '伦理原则制定或翻译版本持有' },
        risk: {
            ko: '현지어 윤리 원칙 미보유는 OECD 뇌물방지협약 및 현지 반부패법(FCPA·UKBA 등)상 내부 통제 체계 부재로 인정되어, 위반 행위 발생 시 조직의 공모 책임이 인정될 수 있습니다.',
            en: 'Lack of localized ethics principles is recognized as the absence of an internal-control system under the OECD Anti-Bribery Convention and local anti-corruption laws (FCPA, UKBA, etc.), potentially extending liability for complicity to the organization upon any violation.',
            ca: '不持有当地语言版本的伦理原则将被认定为《经合组织反贿赂公约》及当地反腐败法(FCPA、UKBA 等)所要求的内部控制体系缺失，违法行为发生时可能追究组织的共谋责任。'
        }
    },
    { no: 11, db_sub_code: 'H2.6v2', category3_code: 'B-1-2',
        category3_name: { ko: '윤리신고 매커니즘', en: 'Ethics Reporting Mechanism', ca: '伦理举报机制' },
        question_no: 'Q2.6',
        indicator: { ko: '공개된 윤리 신고 채널 운영', en: 'Operation of a Publicly Accessible Ethics Reporting Channel', ca: '运营公开的伦理举报渠道' },
        risk: {
            ko: '윤리 신고 채널 미운영은 ISO 37001(반부패 경영시스템) 및 현지 내부고발자 보호법이 요구하는 신고 체계 의무를 위반하며, 위반 은폐 시 조직 책임이 가중될 수 있습니다.',
            en: 'Failure to operate an ethics reporting channel violates the reporting-system obligations under ISO 37001 (Anti-Bribery Management Systems) and local whistleblower-protection laws, and may aggravate organizational liability where violations are concealed.',
            ca: '未运营伦理举报渠道违反《ISO 37001》(反贿赂管理体系)及当地举报人保护法所要求的举报体系义务，且在掩盖违规时组织责任将被加重。'
        }
    }
];

// ────────────────────────────────────────────────────────────────
// core_indicator_campaign_exclusions — Core 지표 중 특정 캠페인(질문지)엔
//   문항 자체가 없어 Core 표/충족판정에서 제외해야 하는 매핑.
//   { db_sub_code: [제외할 campaign_id, ...] }
//
//   왜 필요한가: 채점은 '템플릿 전체 문항'을 돌기 때문에, 질문지에 없는 문항도
//   미답변(not_applicable=False, satisfied=False) → '미충족' 유령 row 로 생성된다.
//   (assess_formula_v2 의 sub_data_contents 가 NULL → return False, False)
//   따라서 'scoring row 없으면 N/A' 예외처리(coreIndicatorStatusByDbCode)를 빠져나가
//   미충족으로 표출되므로, 질문지에 없는 Core 지표는 캠페인 단위로 명시 제외한다.
//
//   H1.49v2 (A-4-1 협력사 행동규범 / Q1.51): 공급망(A-4) 섹션이 없는 질문지에서 제외.
//     - 553 국내-연구소 / 556 해외-권역본부 / 557 해외-연구소 / 558 해외-판매법인
// ────────────────────────────────────────────────────────────────
export const core_indicator_campaign_exclusions = {
    'H1.49v2': [553, 556, 557, 558],
};

// ────────────────────────────────────────────────────────────────
// code_descriptions — Excel "(QESG) 2026 현대차 사업장 평가 ESG 진단 지표 분류_결과보고서용.xlsx"
// '2026 지표' 시트의 'Code 설명' 컬럼 (P5/P6 코드별 준수결과 표 우측 영역) + en/ca 번역
// ────────────────────────────────────────────────────────────────
export const code_descriptions = {
    'A-1-1': {
        ko: '인권 정책을 명문화하여 공표하고, 전담 조직과 교육을 통해 전 임직원에게 내재화해야 한다.',
        en: 'A human rights policy shall be codified and publicly disclosed, and embedded across the workforce through a dedicated organization and training.',
        ca: '应明文制定并公开人权政策,并通过专责组织和培训使其内化于全体员工。'
    },
    'A-1-2': {
        ko: '모든 근로자와 서면 근로계약을 체결하고, 급여명세서를 제공하며, 외국인 근로자에게는 모국어로 된 문서를 교부해야 한다.',
        en: 'A written labor contract shall be signed with every worker, pay statements provided, and documents in their native language issued to foreign workers.',
        ca: '应与所有劳动者签订书面劳动合同,提供工资明细单,并向外籍劳动者交付以其母语编写的文件。'
    },
    'A-1-3': {
        ko: '법정 근로시간을 준수하여 기록·관리하고, 충분한 휴일과 적법한 연장근로 수당을 보장해야 한다.',
        en: 'Statutory working hours shall be observed, recorded, and managed, with sufficient holidays and lawful overtime allowances guaranteed.',
        ca: '应遵守并记录管理法定工时,保障充分的休假和合法的加班津贴。'
    },
    'A-1-4': {
        ko: '법정 최저임금 이상을 지급하고, 생활임금 수준을 지향하는 임금 체계를 갖추어야 한다.',
        en: 'Wages shall be paid at or above the statutory minimum, with a compensation system aspiring to a living-wage level.',
        ca: '应支付不低于法定最低工资的薪酬,并建立以生活工资水平为目标的薪酬体系。'
    },
    'A-1-5': {
        ko: '사내도급사 근로자의 근로조건을 계약 또는 정기 점검을 통해 차별 없이 보호해야 한다.',
        en: 'Working conditions of in-house contractor workers shall be protected without discrimination through contracts or regular monitoring.',
        ca: '应通过合同或定期检查,无差别地保护内部承包公司劳动者的劳动条件。'
    },
    'A-2-1': {
        ko: '직장 내 괴롭힘·비인도적 행위를 금지하고, 공정한 징계 절차와 재발 방지 체계를 운영해야 한다.',
        en: 'Workplace harassment and inhumane treatment shall be prohibited, with fair disciplinary procedures and recurrence-prevention systems in place.',
        ca: '应禁止职场骚扰和非人道行为,并建立公正的惩戒程序和防止再发体系。'
    },
    'A-2-2': {
        ko: '채용 과정에서 일체의 차별을 금지하고, 직무와 무관한 개인정보를 요구하지 않는 공정한 절차를 갖추어야 한다.',
        en: 'All forms of discrimination shall be prohibited in recruitment, and a fair process established that does not request job-irrelevant personal information.',
        ca: '应在招聘过程中禁止一切歧视,并建立不要求与工作无关个人信息的公平程序。'
    },
    'A-2-3': {
        ko: '법적 최소 근로 연령을 엄격히 준수하고, 견습생·인턴 보호를 포함한 아동노동 방지 체계를 운영해야 한다.',
        en: 'The legal minimum working age shall be strictly observed, and a child-labor prevention system — including apprentice and intern protection — operated.',
        ca: '应严格遵守法定最低就业年龄,并运营包括学徒和实习生保护在内的童工防止体系。'
    },
    'A-2-4': {
        ko: '강제노동을 금지하고, 신분증 압수·이동 제한·위약금 부과·채무 속박 등 강압적 행위를 근절해야 한다.',
        en: 'Forced labor shall be prohibited and coercive practices such as ID confiscation, mobility restriction, penalty fees, and debt bondage eliminated.',
        ca: '应禁止强制劳动,并杜绝扣押身份证、限制行动、收取违约金、债务束缚等强制行为。'
    },
    'A-3-1': {
        ko: '공식적인 노사 소통 채널을 운영하고, 임금·근로조건에 대한 정기 협의 및 결사·단체 교섭의 자유를 실질적으로 보장해야 한다.',
        en: 'Formal labor-management communication channels shall be operated, with periodic consultation on wages and conditions and substantive guarantees of freedom of association and collective bargaining.',
        ca: '应运营正式的劳资沟通渠道,定期就工资及劳动条件进行协商,切实保障结社自由及集体谈判权。'
    },
    'A-3-2': {
        ko: '법정교육 외 직무 관련 교육 훈련 프로그램을 제공하고, 연구 인력의 역량을 체계적으로 관리해야 한다.',
        en: 'Beyond statutory training, job-related education programs shall be provided, and the competencies of research personnel systematically managed.',
        ca: '应在法定培训之外提供与职务相关的教育培训项目,并系统地管理研究人员的能力。'
    },
    'A-3-3': {
        ko: '일·가정 양립을 위해 현지법상 임신·출산·육아 관련 권리를 남녀 모두에게 보장해야 한다.',
        en: 'For work-life balance, pregnancy, childbirth, and parenting rights under local law shall be guaranteed equally to all genders.',
        ca: '为兼顾工作与家庭,应依据当地法律向男女员工平等保障怀孕、生育、育儿相关权利。'
    },
    'A-3-4': {
        ko: '사업장 내 시설 및 작업도구의 위생을 정기 관리하고, 노후 설비의 보건 유해 요인을 식별·관리해야 한다.',
        en: 'Workplace facilities and tools shall be hygienically maintained on a regular basis, and health-hazard factors of aging equipment identified and controlled.',
        ca: '应定期管理工作场所内设施及作业工具的卫生,并识别和管控老旧设备的健康危害因素。'
    },
    'A-3-5': {
        ko: '공개된 고충 접수 채널을 운영하고, 현지어 접근성·신고자 보호·독립적 처리·데이터 관리를 보장해야 한다.',
        en: 'A public grievance channel shall be operated with local-language accessibility, whistleblower protection, independent handling, and data management.',
        ca: '应运营公开的申诉受理渠道,保障当地语言可及性、举报人保护、独立处理及数据管理。'
    },
    'A-4-1': {
        ko: '협력사가 준수해야 할 인권·노동 기준을 현지어로 작성하고, 정기적으로 공유하며 교육을 제공해야 한다.',
        en: 'Human rights and labor standards for suppliers shall be drafted in the local language, regularly shared, and supported with training.',
        ca: '应以当地语言制定供应商应遵守的人权及劳动标准,定期共享并提供培训。'
    },
    'A-4-2': {
        ko: '협력사 계약에 행동규범 준수 조항을 포함하고, 정기적 ESG 리스크 점검과 개선 요구·ESG 의사결정 반영을 통해 공급망 인권 리스크를 관리해야 한다.',
        en: 'Supplier contracts shall include code-of-conduct compliance clauses, and supply-chain human rights risks managed through periodic ESG due diligence, improvement requests, and ESG-informed decisions.',
        ca: '应在供应商合同中纳入遵守行为准则的条款,并通过定期 ESG 风险检查、改善要求及在 ESG 决策中的反映,管理供应链人权风险。'
    },
    'A-4-3': {
        ko: '분쟁광물 및 주요 원자재 공급망에서 강제노동·아동노동 등 인권 침해 발생 여부를 정기 실사를 통해 관리해야 한다.',
        en: 'Through periodic due diligence, the occurrence of human-rights violations such as forced labor or child labor in conflict-mineral and key raw-material supply chains shall be managed.',
        ca: '应通过定期实地调查管理冲突矿物及主要原材料供应链中是否存在强制劳动、童工等人权侵害。'
    },
    'A-5-1': {
        ko: '기숙사 위생·안전을 정기 관리하고, 거주 근로자의 이동 자유를 보장하며 외국인 근로자에게 다국어 안전 안내를 제공해야 한다.',
        en: 'Dormitory hygiene and safety shall be regularly managed, residents\' freedom of movement guaranteed, and multilingual safety guidance provided to foreign workers.',
        ca: '应定期管理宿舍卫生与安全,保障居住劳动者的行动自由,并向外籍劳动者提供多语种安全指引。'
    },
    'A-5-2': {
        ko: '식당 시설 및 설비의 소독 및 대청소를 통한 정기적인 위생 관리와 청결 상태 유지 여부를 점검해야 한다.',
        en: 'Cafeteria facilities and equipment shall be hygienically maintained through periodic disinfection and deep cleaning, with cleanliness inspected.',
        ca: '应通过对食堂设施和设备进行消毒及大扫除,实施定期卫生管理并检查清洁状态的维持。'
    },
    'B-1-1': {
        ko: '윤리 원칙을 명문화하여 공표하고, 임직원 교육·서약·담당 조직 운영을 통해 윤리경영을 전사에 내재화해야 한다.',
        en: 'Ethics principles shall be codified and publicly disclosed, and embedded company-wide through employee training, pledges, and a dedicated organization.',
        ca: '应明文制定并公开伦理原则,通过员工培训、宣誓和专责组织的运营,使伦理经营内化于全公司。'
    },
    'B-1-2': {
        ko: '비윤리적 행위를 신고할 수 있는 공개 채널을 운영하고, 현지어 접근성·신고자 보호·처리 절차 공개·인식 제고 활동을 보장해야 한다.',
        en: 'A public channel for reporting unethical conduct shall be operated, with local-language accessibility, whistleblower protection, disclosure of handling procedures, and awareness-raising activities.',
        ca: '应运营可举报非伦理行为的公开渠道,保障当地语言可及性、举报人保护、处理程序公开及认知提升活动。'
    },
    'B-2-1': {
        ko: '그린워싱 리스크 점검 프로세스를 구축하고 정기적 자가점검을 실시하여 환경 관련 주장의 진실성을 보장해야 한다.',
        en: 'A greenwashing risk-screening process shall be established and periodic self-checks performed to ensure the truthfulness of environmental claims.',
        ca: '应建立绿色洗涤(greenwashing)风险检查流程并定期开展自查,确保环境相关主张的真实性。'
    }
};

export default {
    code_level_thresholds,
    code_localized,
    audit_categories,
    answer_localized,
    answers_by_question,
    question_subjects,
    business_site_category,
    benchmark_score,
    core_risk_indicators,
    core_indicator_campaign_exclusions,
    code_descriptions
};
